import type { StyleRule } from '@archipro-design/aria';
import {
    pxToRem,
    pxArrayToRem,
    tileImageClassName,
    professionalTileSlotClassNames,
    tileImageSlotClassNames,
    categorySelectorSlotClassNames,
    labelClassName,
} from '@archipro-design/aria';

const spacer = 50;
const INNER_CONTENT_DELAY = `0s`;
const CONTENT_TRANSITION_DURATION = `0.3s`;
const LAYOUT_TRANSITION_DURATION = `0.3s`;

export const CollapsiblePageLayout: StyleRule<{
    sidePanelWidth: number;
    hideSidePanel?: boolean;
    stickyPanel?: boolean;
}> = ({ theme }) => {
    return {
        width: '100%',
        flexDirection: 'column',
        padding: pxArrayToRem([0, 10]),
        minHeight: '100vh',

        [theme.screen.laptop]: {
            padding: pxArrayToRem([0, 50]),
            overflow: 'visible',
            flexDirection: 'row',
            display: 'flex',
        },
    };
};

export const SidePanelWrap: StyleRule<{
    sidePanelWidth: number;
    hideSidePanel?: boolean;
    stickyPanel?: boolean;
}> = ({ theme, hideSidePanel, sidePanelWidth, stickyPanel }) => {
    return {
        display: 'flex',
        flexDirection: 'column',

        [theme.screen.laptop]: {
            position: stickyPanel ? 'sticky' : 'relative',
            top: stickyPanel ? 0 : 'auto',
            height: stickyPanel ? '100vh' : 'auto',
            maxHeight: stickyPanel ? '100vh' : 'none',
            overflowY: stickyPanel ? 'auto' : 'visible',
            transition: `all ${LAYOUT_TRANSITION_DURATION}`,
            marginRight: hideSidePanel
                ? 0
                : pxToRem((sidePanelWidth + spacer) * -1),
            width: hideSidePanel ? 0 : pxToRem(sidePanelWidth),
            minWidth: hideSidePanel ? 0 : pxToRem(sidePanelWidth),
            transform: `translate(${
                hideSidePanel ? pxToRem((sidePanelWidth + spacer) * -1) : '0'
            }, 0)`,

            [`& .accordion-filters`]: {
                marginTop: pxToRem(60),
            },

            [`& .${categorySelectorSlotClassNames.selectedCategory} .${labelClassName}`]:
                {
                    textDecoration: 'underline',
                },
        },
    };
};

export const AnimatedGridClass = 'collapsible-animated-grid';

export const ContentWrap: StyleRule<{
    sidePanelWidth: number;
    hideSidePanel?: boolean;
    stickyPanel?: boolean;
}> = ({ theme, hideSidePanel, sidePanelWidth }) => ({
    [theme.screen.laptop]: {
        transition: `margin-left ${LAYOUT_TRANSITION_DURATION} cubic-bezier(0.215, 0.61, 0.355, 1)`,
        marginLeft: hideSidePanel ? 0 : pxToRem(sidePanelWidth + spacer),
        width: '100%',
        willChange: 'margin-left',

        [`& .${AnimatedGridClass}`]: {
            transition: CONTENT_TRANSITION_DURATION,
            willChange: 'grid-template-columns',
        },

        [`& .${tileImageClassName}`]: {
            transition: `width ${CONTENT_TRANSITION_DURATION}, height ${CONTENT_TRANSITION_DURATION}`,
            transitionDelay: INNER_CONTENT_DELAY,
        },

        [`& .${tileImageSlotClassNames.imageContainer}`]: {
            transition: `width ${CONTENT_TRANSITION_DURATION}, height ${CONTENT_TRANSITION_DURATION}`,
            transitionDelay: INNER_CONTENT_DELAY,
        },

        [`& .${professionalTileSlotClassNames.logo}`]: {
            transition: `all ${CONTENT_TRANSITION_DURATION}`,
            transitionDelay: INNER_CONTENT_DELAY,
        },

        [`& .${professionalTileSlotClassNames.logo} img`]: {
            transition: `all ${CONTENT_TRANSITION_DURATION}`,
            transitionDelay: INNER_CONTENT_DELAY,
        },
    },
});
